
import { Node, Edge, MarkerType } from 'reactflow';
import { v4 as uuidv4 } from 'uuid';
import { NodeData } from './types';
import { DiGraph, createEmptyNode } from './utils';

// --- Capability contract (defined locally/structurally; the admin app consumes
// the same shape) ------------------------------------------------------------

export interface EditorToolResult {
  ok: boolean;
  result?: any;
  rejected?: boolean;
  reason?: string;
}

export interface EditorAiTool {
  name: string;
  description: string;
  /** JSON Schema object describing the tool's args. */
  parameters: Record<string, any>;
}

export interface EditorAiCapability {
  kind: string;
  guidelines: string;
  tools: EditorAiTool[];
  invoke: (toolName: string, args: any) => Promise<EditorToolResult>;
}

// --- Live model accessor: the capability reads/mutates the editor's REAL
// reactflow state through these, so every change re-renders the editor and is
// validated/rejected by the editor's own rules. -----------------------------

export interface GroupModelAccess {
  /** Current nodes (live). */
  getNodes: () => Node<NodeData>[];
  /** Current edges (live). */
  getEdges: () => Edge[];
  /** The designated root group id (cannot be deleted / must stay the single root). */
  getRootId: () => string;
  /** Push a single undo snapshot before a mutation. */
  takeSnapshot: () => void;
  /** Apply new nodes (replaces the node array). */
  setNodes: (nodes: Node<NodeData>[]) => void;
  /** Apply new edges (replaces the edge array). */
  setEdges: (edges: Edge[]) => void;

  // --- Viewport / selection (read-only on the model; they only move the camera
  // and the editor's selection/highlight state, never mutate the hierarchy). The
  // host wires these to the live reactflow instance + selection setter. All are
  // optional so a host that does not render a viewport can still expose the
  // model tools; the capability degrades gracefully ({ok:false,reason}) when a
  // viewport tool is invoked without backing support. -------------------------
  /** Center + zoom the viewport on a node (id assumed to exist). */
  focusNode?: (nodeId: string, zoom?: number) => boolean;
  /** Replace the editor's selection/highlight with these node ids. */
  setSelection?: (nodeIds: string[]) => void;
  /** Fit the whole hierarchy into the viewport. Returns false if the viewport is not ready. */
  fitView?: () => boolean;
  /** Set the zoom level (absolute) or step in/out. */
  setZoom?: (mode: 'in' | 'out' | 'level', level?: number) => boolean;
}

// Fields a caller is allowed to set when adding/updating a node. We intentionally
// keep this to the user-meaningful, serialisable fields and never let the model
// write internal flags like inCycle / isRootNode.
const EDITABLE_FIELDS = [
  'name',
  'description',
  'iconPath',
  'color',
  'isActive',
  'isDeleted',
  'isSuspended',
  'deletionReason',
  'deletionMessage',
  'suspensionReason',
  'suspensionMessage',
  'attributes',
  'memberCount'
] as const;

type EditableField = typeof EDITABLE_FIELDS[number];

const NODE_TYPE = 'group';

function reject(reason: string): EditorToolResult {
  return { ok: false, rejected: true, reason };
}

function ok(result?: any): EditorToolResult {
  return { ok: true, result };
}

/** Compact serialisation of one node for the model to read. */
function nodeToCompact(node: Node<NodeData>, edges: Edge[]) {
  const children = edges.filter(e => e.source === node.id).map(e => e.target);
  const parentEdge = edges.find(e => e.target === node.id);
  return {
    id: node.id,
    name: node.data.name,
    description: node.data.description,
    iconPath: node.data.iconPath,
    isActive: node.data.isActive,
    isDeleted: node.data.isDeleted,
    isSuspended: node.data.isSuspended,
    memberCount: node.data.memberCount,
    isRoot: node.data.isRootNode,
    parent: parentEdge ? parentEdge.source : null,
    children
  };
}

/**
 * The editor's REAL validation, mirroring Inner.tsx `evaluateGraph`. Returns the
 * full list of validation errors for a candidate model, each tagged with whether
 * it is a hard blocker for a mutation.
 *
 * - acyclicity (cycle) and the one-parent rule are HARD blockers: the editor's
 *   `onConnect` refuses a second parent and a cycle is never a saveable state, so
 *   a tool must never apply a mutation that introduces one.
 * - disconnection (more than one topological root, or the single root not being
 *   the designated root) is a SOFT error: the editor itself permits this as a
 *   transient editing state (you may drop a detached node then re-parent it), it
 *   only disables Save. A mutation may leave the model in this state.
 */
interface ValidationIssue {
  message: string;
  blocking: boolean;
}

function evaluateGraphErrors(
  nodes: Node<NodeData>[],
  edges: Edge[],
  rootId: string
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const graph = new DiGraph();
  nodes.forEach(n => graph.addVertex(n.id));
  edges.forEach(e => graph.addEdge(e.source, e.target));

  // (a) Acyclicity — same DiGraph.findCycles the editor uses. HARD blocker.
  if (graph.findCycles().length > 0) {
    issues.push({ message: 'Cycles detected in hierarchy.', blocking: true });
  }

  // (b) One-parent rule — the editor's onConnect refuses a node that already has
  // a parent. Any node with more than one incoming edge violates it. HARD blocker.
  const inDegree = new Map<string, number>();
  edges.forEach(e => inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1));
  const multiParent = nodes.filter(n => (inDegree.get(n.id) || 0) > 1);
  if (multiParent.length > 0) {
    issues.push({ message: 'A group can only have one parent.', blocking: true });
  }

  // (c) Single connected root — reported like the editor's validationError, but
  // SOFT (transient editing state).
  const targets = new Set(edges.map(e => e.target));
  const topologicalRoots = nodes.filter(n => !targets.has(n.id));
  if (nodes.length > 0 && topologicalRoots.length !== 1) {
    issues.push({
      message: `Graph disconnected: Found ${topologicalRoots.length} root-like nodes. Ensure all groups are connected.`,
      blocking: false
    });
  } else if (
    topologicalRoots.length === 1 &&
    rootId &&
    topologicalRoots[0].id !== rootId
  ) {
    issues.push({
      message: `The single root must be the designated root group (${rootId}).`,
      blocking: false
    });
  }

  return issues;
}

/** Flat list of validation error messages (for the `validate` tool). */
function computeValidation(nodes: Node<NodeData>[], edges: Edge[], rootId: string): string[] {
  return evaluateGraphErrors(nodes, edges, rootId).map(i => i.message);
}

/**
 * Run the editor's REAL validation on the candidate model and apply it ONLY when
 * the mutation does not INTRODUCE a new blocking error. Compares the blocking
 * errors of the current model against the candidate: if the candidate carries a
 * blocking error (cycle / one-parent) that the current model did not, the
 * mutation is rejected with the validator's reason and nothing is written.
 */
function applyValidated(
  access: GroupModelAccess,
  nodes: Node<NodeData>[],
  edges: Edge[]
): EditorToolResult {
  const rootId = access.getRootId();

  const before = new Set(
    evaluateGraphErrors(access.getNodes(), access.getEdges(), rootId)
      .filter(i => i.blocking)
      .map(i => i.message)
  );
  const candidate = evaluateGraphErrors(nodes, edges, rootId).filter(i => i.blocking);
  const introduced = candidate.find(i => !before.has(i.message));
  if (introduced) {
    return reject(introduced.message);
  }

  access.takeSnapshot();
  access.setNodes(nodes);
  access.setEdges(edges);
  return ok();
}

function pickEditableData(input: any): { data: Partial<NodeData>; error?: string } {
  if (input == null || typeof input !== 'object') {
    return { data: {}, error: 'Expected an object of fields.' };
  }
  const data: Partial<NodeData> = {};
  for (const key of Object.keys(input)) {
    if (!EDITABLE_FIELDS.includes(key as EditableField)) {
      return { data: {}, error: `Unknown or non-editable field "${key}".` };
    }
    (data as any)[key] = input[key];
  }
  if ('name' in data && (typeof data.name !== 'string' || !data.name.trim())) {
    return { data: {}, error: 'Field "name" must be a non-empty string.' };
  }
  if ('attributes' in data && !Array.isArray(data.attributes)) {
    return { data: {}, error: 'Field "attributes" must be an array.' };
  }
  if ('memberCount' in data && typeof data.memberCount !== 'number') {
    return { data: {}, error: 'Field "memberCount" must be a number.' };
  }
  return { data };
}

const GUIDELINES =
  'This is the GROUP / hierarchy editor. The model is a directed acyclic tree of ' +
  'group nodes (the only node type is "group") connected by parent->child edges. ' +
  'Each group has a single parent (one-parent rule) and exactly one designated ' +
  'root group exists and cannot be deleted. Valid editable node fields are: name ' +
  '(required, non-empty), description, iconPath, color, isActive, isDeleted, ' +
  'isSuspended, deletion/suspension reason+message, attributes (array of ' +
  '{role,value}), memberCount. Tools route through the editor\'s real mutation ' +
  'and validation logic: connecting a node that already has a parent, creating a ' +
  'cycle, deleting the root, or unknown node ids/fields are rejected with a ' +
  'reason and the model is left unchanged. Navigation tools (focus_node, ' +
  'select_nodes, clear_selection, fit_view, zoom) only move the viewport or the ' +
  'editor selection so the user can see what you mean — they never change the ' +
  'hierarchy, and reject unknown node ids without throwing.';

export function buildGroupAiCapability(access: GroupModelAccess): EditorAiCapability {
  const tools: EditorAiTool[] = [
    {
      name: 'get_model',
      description: 'Return the current group hierarchy (nodes + parent/child links) as compact JSON.',
      parameters: { type: 'object', properties: {}, additionalProperties: false }
    },
    {
      name: 'list_node_types',
      description: 'List the node/element types this editor supports.',
      parameters: { type: 'object', properties: {}, additionalProperties: false }
    },
    {
      name: 'add_node',
      description: 'Add a new group node. Optionally connect it under a parent group (the parent must exist and the child must not already have a parent).',
      parameters: {
        type: 'object',
        properties: {
          name: { type: 'string', description: 'Group name (required, non-empty).' },
          parentId: { type: 'string', description: 'Optional id of the parent group to connect under.' },
          description: { type: 'string' },
          iconPath: { type: 'string' },
          memberCount: { type: 'number' }
        },
        required: ['name'],
        additionalProperties: true
      }
    },
    {
      name: 'update_node',
      description: 'Update editable fields of an existing group node by id.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Target node id.' },
          fields: { type: 'object', description: 'Map of editable fields to set.' }
        },
        required: ['id', 'fields'],
        additionalProperties: false
      }
    },
    {
      name: 'delete_node',
      description: 'Delete a group node by id (and any edges touching it). The designated root cannot be deleted.',
      parameters: {
        type: 'object',
        properties: { id: { type: 'string' } },
        required: ['id'],
        additionalProperties: false
      }
    },
    {
      name: 'connect_nodes',
      description: 'Connect a parent group to a child group (parent->child edge). Rejected if the child already has a parent or the link would create a cycle.',
      parameters: {
        type: 'object',
        properties: {
          parentId: { type: 'string' },
          childId: { type: 'string' }
        },
        required: ['parentId', 'childId'],
        additionalProperties: false
      }
    },
    {
      name: 'disconnect_nodes',
      description: 'Remove the parent->child edge between two groups.',
      parameters: {
        type: 'object',
        properties: {
          parentId: { type: 'string' },
          childId: { type: 'string' }
        },
        required: ['parentId', 'childId'],
        additionalProperties: false
      }
    },
    {
      name: 'validate',
      description: 'Run the editor validation rules and return the list of current validation errors (empty = valid).',
      parameters: { type: 'object', properties: {}, additionalProperties: false }
    },
    {
      name: 'replace_model',
      description: 'Replace the whole hierarchy from JSON ({ nodes: [{id?,name,parent?,...}] }). Validated before applying; rejected if it would be cyclic or has no/ambiguous root.',
      parameters: {
        type: 'object',
        properties: {
          nodes: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                id: { type: 'string' },
                name: { type: 'string' },
                parent: { type: 'string' }
              },
              required: ['name']
            }
          }
        },
        required: ['nodes'],
        additionalProperties: false
      }
    },

    // --- Navigation / viewport / selection (no model mutation) ----------------
    {
      name: 'focus_node',
      description: 'Center the viewport on a single group node and select it (so the user sees what the assistant is referring to). Does not change the hierarchy. Rejected if the node id does not exist.',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Id of the group node to focus.' },
          zoom: { type: 'number', description: 'Optional zoom level to apply while centering (clamped to 0.1..2).', minimum: 0.1, maximum: 2 }
        },
        required: ['id'],
        additionalProperties: false
      }
    },
    {
      name: 'select_nodes',
      description: 'Highlight/select one or more group nodes in the editor (so the user sees which groups the assistant means). Does not change the hierarchy. Rejected if any id is unknown.',
      parameters: {
        type: 'object',
        properties: {
          ids: {
            type: 'array',
            items: { type: 'string' },
            minItems: 1,
            description: 'Ids of the group nodes to select.'
          }
        },
        required: ['ids'],
        additionalProperties: false
      }
    },
    {
      name: 'clear_selection',
      description: 'Clear the current selection/highlight in the editor. Does not change the hierarchy.',
      parameters: { type: 'object', properties: {}, additionalProperties: false }
    },
    {
      name: 'fit_view',
      description: 'Zoom/pan the viewport to fit the whole hierarchy on screen. Does not change the hierarchy.',
      parameters: { type: 'object', properties: {}, additionalProperties: false }
    },
    {
      name: 'zoom',
      description: 'Adjust the viewport zoom: step in, step out, or set an absolute level. Does not change the hierarchy.',
      parameters: {
        type: 'object',
        properties: {
          direction: {
            type: 'string',
            enum: ['in', 'out', 'level'],
            description: 'in/out to step, or "level" to set an absolute zoom (requires "level").'
          },
          level: { type: 'number', description: 'Absolute zoom level when direction="level" (clamped to 0.1..2).', minimum: 0.1, maximum: 2 }
        },
        required: ['direction'],
        additionalProperties: false
      }
    }
  ];

  const invoke = async (toolName: string, args: any): Promise<EditorToolResult> => {
    const nodes = access.getNodes();
    const edges = access.getEdges();
    const rootId = access.getRootId();

    switch (toolName) {
      case 'get_model': {
        return ok({
          rootId,
          nodes: nodes.map(n => nodeToCompact(n, edges))
        });
      }

      case 'list_node_types': {
        return ok({
          nodeTypes: [
            {
              type: NODE_TYPE,
              description: 'A group / hierarchy node. Connected to its parent by a single parent->child edge.',
              editableFields: [...EDITABLE_FIELDS]
            }
          ]
        });
      }

      case 'add_node': {
        const { data, error } = pickEditableData(
          // pull only editable keys, ignore parentId which is structural
          Object.fromEntries(
            Object.entries(args || {}).filter(([k]) => k !== 'parentId')
          )
        );
        if (error) return reject(error);
        if (!data.name) return reject('Field "name" is required.');

        const parentId = args?.parentId;
        if (parentId != null) {
          if (!nodes.some(n => n.id === parentId)) {
            return reject(`Parent node "${parentId}" does not exist.`);
          }
        }

        const id = uuidv4();
        const newNode = createEmptyNode(id, { x: 0, y: 0 }, false);
        newNode.data = { ...newNode.data, ...data };

        const nextNodes = [...nodes, newNode];
        let nextEdges = edges;
        if (parentId != null) {
          nextEdges = [
            ...edges,
            {
              id: `e-${parentId}-${id}`,
              source: parentId,
              target: id,
              type: 'base',
              markerEnd: { type: MarkerType.ArrowClosed }
            }
          ];
        }

        const res = applyValidated(access, nextNodes, nextEdges);
        if (!res.ok) return res;
        return ok({ id });
      }

      case 'update_node': {
        const id = args?.id;
        if (!nodes.some(n => n.id === id)) {
          return reject(`Node "${id}" does not exist.`);
        }
        const { data, error } = pickEditableData(args?.fields);
        if (error) return reject(error);
        if (Object.keys(data).length === 0) {
          return reject('No editable fields supplied.');
        }
        const nextNodes = nodes.map(n =>
          n.id === id ? { ...n, data: { ...n.data, ...data } } : n
        );
        const res = applyValidated(access, nextNodes, edges);
        if (!res.ok) return res;
        return ok({ id });
      }

      case 'delete_node': {
        const id = args?.id;
        if (!nodes.some(n => n.id === id)) {
          return reject(`Node "${id}" does not exist.`);
        }
        if (id === rootId) {
          return reject('The designated root group cannot be deleted.');
        }
        const nextNodes = nodes.filter(n => n.id !== id);
        const nextEdges = edges.filter(e => e.source !== id && e.target !== id);
        const res = applyValidated(access, nextNodes, nextEdges);
        if (!res.ok) return res;
        return ok({ id });
      }

      case 'connect_nodes': {
        const { parentId, childId } = args || {};
        if (!nodes.some(n => n.id === parentId)) {
          return reject(`Parent node "${parentId}" does not exist.`);
        }
        if (!nodes.some(n => n.id === childId)) {
          return reject(`Child node "${childId}" does not exist.`);
        }
        if (parentId === childId) {
          return reject('A group cannot be connected to itself.');
        }
        if (edges.some(e => e.target === childId)) {
          return reject('A group can only have one parent.');
        }
        const nextEdges = [
          ...edges,
          {
            id: `e-${parentId}-${childId}`,
            source: parentId,
            target: childId,
            type: 'base',
            markerEnd: { type: MarkerType.ArrowClosed }
          }
        ];
        const res = applyValidated(access, nodes, nextEdges);
        if (!res.ok) return res;
        return ok({ parentId, childId });
      }

      case 'disconnect_nodes': {
        const { parentId, childId } = args || {};
        const exists = edges.some(e => e.source === parentId && e.target === childId);
        if (!exists) {
          return reject(`No edge from "${parentId}" to "${childId}".`);
        }
        const nextEdges = edges.filter(
          e => !(e.source === parentId && e.target === childId)
        );
        const res = applyValidated(access, nodes, nextEdges);
        if (!res.ok) return res;
        return ok({ parentId, childId });
      }

      case 'validate': {
        const errors = computeValidation(nodes, edges, rootId);
        return ok({ valid: errors.length === 0, errors });
      }

      case 'replace_model': {
        const input = args?.nodes;
        if (!Array.isArray(input)) {
          return reject('Expected { nodes: [...] }.');
        }
        // Assign ids, validate names.
        const built: Node<NodeData>[] = [];
        const idByIndex: string[] = [];
        const ids = new Set<string>();
        for (let i = 0; i < input.length; i++) {
          const raw = input[i];
          if (raw == null || typeof raw !== 'object') {
            return reject(`nodes[${i}] is not an object.`);
          }
          if (typeof raw.name !== 'string' || !raw.name.trim()) {
            return reject(`nodes[${i}].name must be a non-empty string.`);
          }
          const id = typeof raw.id === 'string' && raw.id ? raw.id : uuidv4();
          if (ids.has(id)) return reject(`Duplicate node id "${id}".`);
          ids.add(id);
          idByIndex[i] = id;
          const node = createEmptyNode(id, { x: 0, y: 0 }, false);
          node.data = { ...node.data, name: raw.name };
          if (typeof raw.description === 'string') node.data.description = raw.description;
          if (typeof raw.iconPath === 'string') node.data.iconPath = raw.iconPath;
          built.push(node);
        }
        // Build edges from parent refs.
        const nextEdges: Edge[] = [];
        for (let i = 0; i < input.length; i++) {
          const parent = input[i].parent;
          if (parent == null) continue;
          if (!ids.has(parent)) {
            return reject(`nodes[${i}].parent "${parent}" is not a node in the model.`);
          }
          const childId = idByIndex[i];
          if (nextEdges.some(e => e.target === childId)) {
            return reject(`Node "${childId}" has more than one parent.`);
          }
          nextEdges.push({
            id: `e-${parent}-${childId}`,
            source: parent,
            target: childId,
            type: 'base',
            markerEnd: { type: MarkerType.ArrowClosed }
          });
        }
        // Mark the root if it matches the designated root id.
        built.forEach(n => {
          n.data.isRootNode = n.id === rootId;
        });
        const errors = computeValidation(built, nextEdges, rootId);
        if (errors.length > 0) {
          return reject(errors.join(' '));
        }
        access.takeSnapshot();
        access.setNodes(built);
        access.setEdges(nextEdges);
        return ok({ nodeCount: built.length });
      }

      case 'focus_node': {
        const id = args?.id;
        const target = nodes.find(n => n.id === id);
        if (!target) {
          return reject(`Node "${id}" does not exist.`);
        }
        if (!access.focusNode) {
          return reject('Viewport not available in this host.');
        }
        let zoom: number | undefined = args?.zoom;
        if (zoom != null) {
          if (typeof zoom !== 'number' || Number.isNaN(zoom)) {
            return reject('Field "zoom" must be a number.');
          }
          zoom = Math.min(2, Math.max(0.1, zoom));
        }
        const moved = access.focusNode(id, zoom);
        if (!moved) {
          return reject(`Could not focus node "${id}" (not on the current canvas).`);
        }
        return ok({ id, zoom: zoom ?? null });
      }

      case 'select_nodes': {
        const ids = args?.ids;
        if (!Array.isArray(ids) || ids.length === 0) {
          return reject('Field "ids" must be a non-empty array of node ids.');
        }
        const known = new Set(nodes.map(n => n.id));
        const missing = ids.filter((x: any) => !known.has(x));
        if (missing.length > 0) {
          return reject(`Unknown node id(s): ${missing.join(', ')}.`);
        }
        if (!access.setSelection) {
          return reject('Selection not available in this host.');
        }
        access.setSelection(ids);
        return ok({ selected: ids });
      }

      case 'clear_selection': {
        if (!access.setSelection) {
          return reject('Selection not available in this host.');
        }
        access.setSelection([]);
        return ok({ selected: [] });
      }

      case 'fit_view': {
        if (!access.fitView) {
          return reject('Viewport not available in this host.');
        }
        if (!access.fitView()) {
          return reject('Could not fit view (viewport not ready).');
        }
        return ok();
      }

      case 'zoom': {
        const direction = args?.direction;
        if (direction !== 'in' && direction !== 'out' && direction !== 'level') {
          return reject('Field "direction" must be one of: in, out, level.');
        }
        let level: number | undefined = args?.level;
        if (direction === 'level') {
          if (typeof level !== 'number' || Number.isNaN(level)) {
            return reject('direction="level" requires a numeric "level".');
          }
          level = Math.min(2, Math.max(0.1, level));
        }
        if (!access.setZoom) {
          return reject('Viewport not available in this host.');
        }
        const applied = access.setZoom(direction, level);
        if (!applied) {
          return reject('Could not apply zoom (viewport not ready).');
        }
        return ok({ direction, level: direction === 'level' ? level : null });
      }

      default:
        return reject(`Unknown tool "${toolName}".`);
    }
  };

  return {
    kind: 'group',
    guidelines: GUIDELINES,
    tools,
    invoke
  };
}
